// @flow

// Reference
// https://github.com/powerreviews/pwr-commons/blob/master/pwr-commons-core/src/main/java/com/powerreviews/commons/core/enumeration/Channel.java
import type {
  ChannelType,
  BrandEngageType,
  BrandEngageTier,
} from './types.js.flow';
import kebabCase from 'lodash/kebabCase';

export type ChannelInfo = {
  label: string,
  description: string,
  type: ChannelType,
  ugctype: string,
  contentUgctype: string,
  brandEngageType?: BrandEngageType,
  brandEngageTier?: BrandEngageTier,
  brandEngageBrandActionable?: boolean,
  worklist?: string,
};

const moderationChannels = {
  SafetyAlert: {
    label: 'Safety Alerts',
    description:
      'Reviews moderated by PowerReviews that contain an important safety issue from the past 30 days.',
    type: 'moderation',
    ugctype: 'Review',
    contentUgctype: 'Review',
  },
  SafetyConcern: {
    label: 'Safety Concerns',
    description:
      'Reviews moderated by PowerReviews that contain a safety concern from the past 30 days.',
    type: 'moderation',
    ugctype: 'Review',
    contentUgctype: 'Review',
  },
  SafetyAlertQuestion: {
    label: 'Safety Alerts',
    description:
      'Questions moderated by PowerReviews that contain an important safety issue from the past 30 days.',
    type: 'moderation',
    ugctype: 'Question',
    contentUgctype: 'Question',
  },
  SafetyConcernQuestion: {
    label: 'Safety Concerns',
    description:
      'Questions moderated by PowerReviews that contain a safety concern from the past 30 days.',
    type: 'moderation',
    ugctype: 'Question',
    contentUgctype: 'Question',
  },
  CustomerServiceIssue: {
    label: 'Customer Service Issue',
    description:
      'Reviews moderated by PowerReviews that contain a customer service issue from the past 30 days.',
    type: 'moderation',
    ugctype: 'Review',
    contentUgctype: 'Review',
  },
  AwaitingClientModeration: {
    label: 'Awaiting Moderation',
    description:
      'All reviews moderated by PowerReviews that your team has not moderated from the past 30 days.',
    type: 'moderation',
    ugctype: 'Review',
    contentUgctype: 'Review',
    worklist:
      'Reviews from the Awaiting Moderation Channel that have not been responded to or moderated by your team.',
  },
  EscalatedReview: {
    label: 'Escalated Reviews',
    description:
      'All reviews that contain words included in your escalated words list, and reviews that are escalated internally from the past 30 days.',
    type: 'moderation',
    ugctype: 'Review',
    contentUgctype: 'Review',
    worklist:
      'Reviews from the Escalated Reviews Channel that have not been responded to or moderated by your team.',
  },
  WebIssue: {
    label: 'Web Issue',
    description:
      'Reviews moderated by PowerReviews that describe a web issue from the past 30 days.',
    type: 'moderation',
    ugctype: 'Review',
    contentUgctype: 'Review',
  },
  LegalConcern: {
    label: 'Legal Concern',
    description:
      'Reviews moderated by PowerReviews that contain a legal concern from the past 30 days.',
    type: 'moderation',
    ugctype: 'Review',
    contentUgctype: 'Review',
  },
  LowRated: {
    label: 'Low Rated',
    description:
      'All reviews that contain a low star rating from the past 30 days.',
    type: 'moderation',
    ugctype: 'Review',
    contentUgctype: 'Review',
    worklist:
      'Reviews from the Low Rated Channel that have not been responded to or moderated by your team.',
  },
  SocialCollection: {
    label: 'Social Collection',
    description:
      'Images and Videos collected and approved through Social Collection content streams.',
    type: 'moderation',
    ugctype: 'Review',
    contentUgctype: 'Review',
    worklist:
      'Images and Videos from the Social Collection Channel that have not been moderated by your team.',
  },
  UnansweredQuestion: {
    label: 'Unanswered Questions',
    description: 'All questions awaiting an answer from the past 30 days.',
    type: 'moderation',
    ugctype: 'Question',
    contentUgctype: 'Question',
    worklist:
      'Questions from the Unanswered Questions Channel that have not been answered, moderated by your team, or assigned to another team member.',
  },
  AssignedQnA: {
    label: 'Assigned Questions',
    description: 'Questions that have been assigned to you.',
    type: 'moderation',
    ugctype: 'Question',
    contentUgctype: 'Question',
  },
  AssignedReview: {
    label: 'Assigned Reviews',
    description: 'Reviews that have been assigned to you.',
    type: 'moderation',
    ugctype: 'Review',
    contentUgctype: 'Review',
  },
  UpdatedReview: {
    label: 'Updated Reviews',
    description: 'Reviews updated by your customers in the past 30 days.',
    type: 'moderation',
    ugctype: 'Review',
    contentUgctype: 'Review',
    worklist:
      'Reviews from the Updated Reviews Channel that have not been moderated by your team.',
  },
};

const brandEngageBrandChannels = {
  NoBrandAnswer: {
    label: 'Questions Without Brand Answers',
    description:
      'Questions you haven’t answered about your brand’s products from your retailers.',
    type: 'brand_engage',
    brandEngageType: 'brand',
    ugctype: 'Question',
    contentUgctype: 'Question',
    brandEngageBrandActionable: true,
  },
  LowRatedWithNoResponse: {
    label: 'Reviews With Low Ratings from Retailer Partners',
    description: 'Reviews that contain ratings less than 3 stars.',
    type: 'brand_engage',
    brandEngageType: 'brand',
    ugctype: 'Review',
    contentUgctype: 'Review',
    brandEngageBrandActionable: true,
  },
  BrandEngageResponse: {
    label: 'Your Review Responses',
    description:
      "Responses to your brand's reviews that you submitted to your retailers.",
    type: 'brand_engage',
    brandEngageType: 'brand',
    ugctype: 'Review',
    contentUgctype: 'Response',
  },
  BrandEngageAnswer: {
    label: 'Your Answers',
    description:
      'Answers to your brand’s questions that you submitted to your retailers.',
    type: 'brand_engage',
    brandEngageType: 'brand',
    ugctype: 'Question',
    contentUgctype: 'Answer',
  },
};

const brandEngageRetailerChannels = {
  RetailerBrandEngageAnswer: {
    label: 'Brand Question Answers',
    description: 'Answers your brand partners submitted to customer questions.',
    type: 'brand_engage',
    brandEngageType: 'retailer',
    ugctype: 'Question',
    contentUgctype: 'Answer',
  },
  RetailerBrandEngageResponse: {
    label: 'Brand Review Responses',
    description:
      'Review responses your brand partners submitted to customer reviews.',
    type: 'brand_engage',
    brandEngageType: 'retailer',
    ugctype: 'Review',
    contentUgctype: 'Response',
  },
};

const channelInfoByName: { [string]: ChannelInfo } = {
  ...moderationChannels,
  ...brandEngageBrandChannels,
  ...brandEngageRetailerChannels,
};

type ModerationChannelNames = $Keys<typeof moderationChannels>;
type BrandEngageBrandChannelNames = $Keys<typeof brandEngageBrandChannels>;
type BrandEngageRetailerChannelNames = $Keys<
  typeof brandEngageRetailerChannels
>;

export type ChannelName =
  | ModerationChannelNames
  | BrandEngageBrandChannelNames
  | BrandEngageRetailerChannelNames;
export type Channel = { name: ChannelName, slug: string } & ChannelInfo;

export const channelByName: { [ChannelName]: Channel } = Object.keys(
  channelInfoByName
).reduce((acc, name) => {
  const info = channelInfoByName[name];

  acc[name] = {
    name,
    slug: info.slug ? info.slug : kebabCase(info.label),
    ...info,
  };

  return acc;
}, {});

export const DefaultModerationChannels: Array<ModerationChannelNames> = [
  'SafetyAlert',
  'SafetyConcern',
  'CustomerServiceIssue',
  'EscalatedReview',
  'WebIssue',
  'LegalConcern',
  'UnansweredQuestion',
  'AssignedQnA',
  'LowRated',
  'AssignedReview',
  'SafetyAlertQuestion',
  'SafetyConcernQuestion',
  'UpdatedReview',
];

export function isModerationChannel(ch: ChannelInfo) {
  return ch.type === 'moderation';
}

export function isBrandEngageChannel(ch: ChannelInfo) {
  return ch.type === 'brand_engage';
}

export function isBrandEngageBrandChannel(ch: ChannelInfo) {
  return isBrandEngageChannel(ch) && ch.brandEngageType === 'brand';
}

export function isBrandEngageRetailerChannel(ch: ChannelInfo) {
  return isBrandEngageChannel(ch) && ch.brandEngageType === 'retailer';
}

export const BrandChannelNames: Array<ChannelName> = Object.keys(
  brandEngageBrandChannels
);

export const RetailerChannelNames: Array<ChannelName> = Object.keys(
  brandEngageRetailerChannels
);



// WEBPACK FOOTER //
// ./src/containers/channels/ChannelInfo.js